import { colourOf, fmtTime, weatherLook, weekdayLabels } from "./palette";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const shift = (day, by) => {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + by);
  return iso(d);
};

const spoken = (day) =>
  new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

export const MonthGrid = ({ cells, todayISO, picked, onPick, events, reminders, plan,
                            activity, letters, weather, holidays, accent, clock, density,
                            weekStarts, dragId, setDragId, onDrop }) => {
  const busiest = Math.max(1, ...Object.values(activity));
  const compact = density === "compact";

  const move = (e) => {
    const steps = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const by = steps[e.key];
    if (!by) return;
    e.preventDefault();
    const next = shift(picked || todayISO, by);
    onPick(next);
    requestAnimationFrame(() => {
      document.querySelector(`[data-testid="calendar-day-${next}"]`)?.focus();
    });
  };

  return (
    <div className={`mt-6 rounded-3xl border border-white/10 bg-panel ${compact ? "p-3 sm:p-4" : "p-5 sm:p-7"}`}>
      <div className="grid grid-cols-7 gap-2 text-center text-[11px] uppercase tracking-widest text-ink-soft">
        {weekdayLabels(weekStarts).map((d) => (
          <span key={d}>
            <span className="hidden sm:inline">{d}</span>
            <span className="sm:hidden">{d.slice(0, 1)}</span>
          </span>
        ))}
      </div>

      <div
        className="mt-2 grid grid-cols-7 gap-2"
        data-testid="calendar-grid"
        role="grid"
        aria-label="Month grid — use the arrow keys to move between days"
        onKeyDown={move}
      >
        {cells.map((day, i) => {
          if (!day) return <span key={`pad-${i}`} role="presentation" />;
          const key = iso(day);
          const opens = activity[key] || 0;
          const kept = letters[key] || 0;
          const isToday = key === todayISO;
          const ahead = key > todayISO;
          const dayReminders = reminders.filter((r) => r.day === key);
          const dayEvents = events.filter((e) => e.day === key);
          const planned = plan?.days.includes(day.getDay() === 0 ? 6 : day.getDay() - 1);
          const practised = plan?.practised.includes(key);
          const sky = weather[key];
          const feast = holidays?.[key];
          const Look = sky ? weatherLook(sky.code) : null;
          return (
            <button
              key={key}
              onClick={() => onPick(key)}
              onDragOver={(e) => dragId && e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onDrop(key);
              }}
              data-testid={`calendar-day-${key}`}
              role="gridcell"
              tabIndex={key === (picked || todayISO) ? 0 : -1}
              aria-selected={key === picked}
              aria-current={isToday ? "date" : undefined}
              aria-label={`${spoken(key)}${feast ? `, ${feast}` : ""}${
                dayEvents.length ? `, ${dayEvents.length} event${dayEvents.length === 1 ? "" : "s"}` : ", nothing on"
              }${dayReminders.some((r) => !r.done) ? ", a reminder waiting" : ""}`}
              title={feast || `${opens} prompt${opens === 1 ? "" : "s"}${kept ? `, ${kept} letter${kept === 1 ? "" : "s"}` : ""}`}
              className={`${compact ? "min-h-[64px]" : "aspect-square"} rounded-xl border p-1.5 text-start transition-colors focus:outline-none focus-visible:ring-2 ${
                ahead && !sky ? "opacity-70" : ""
              } ${planned && !practised && !ahead ? "border-dashed" : ""}`}
              style={{
                borderColor: picked === key ? accent : isToday ? `${accent}99`
                  : planned && !practised && !ahead ? "rgba(74,222,128,.35)" : "rgba(255,255,255,.10)",
                backgroundColor: opens ? `${accent}${Math.round((0.12 + (opens / busiest) * 0.4) * 255).toString(16).padStart(2, "0")}` : undefined,
              }}
            >
              <span className="flex items-start justify-between gap-1">
                <span className="text-[11px] tabular-nums text-ink-soft">{day.getDate()}</span>
                {Look && (
                  <span className="flex items-center gap-0.5" title={Look.label}>
                    <Look.Icon className="h-3 w-3" style={{ color: Look.tint }} />
                    <span className="text-[9px] tabular-nums text-ink-soft">{sky.max}°</span>
                  </span>
                )}
              </span>
              {feast && (
                <span className="mt-0.5 block truncate text-[9px] font-semibold text-violet-300"
                      data-testid={`holiday-${key}`}>
                  {feast}
                </span>
              )}
              <span className="mt-0.5 block space-y-0.5">
                {dayEvents.slice(0, compact ? 1 : 2).map((ev) => (
                  <span
                    key={`${ev.event_id}-${ev.day}`}
                    draggable
                    onDragStart={(e) => {
                      e.stopPropagation();
                      setDragId(ev.event_id);
                    }}
                    onDragEnd={() => setDragId(null)}
                    title={`${ev.all_day ? "All day" : fmtTime(ev.start, clock)} · ${ev.title}`}
                    data-testid={`event-chip-${ev.event_id}`}
                    className="block truncate rounded px-1 text-[9px] font-semibold text-page"
                    style={{ backgroundColor: colourOf(ev.colour) }}
                  >
                    {ev.all_day ? ev.title : `${fmtTime(ev.start, clock)} ${ev.title}`}
                  </span>
                ))}
                {dayEvents.length > (compact ? 1 : 2) && (
                  <span className="block text-[9px] text-ink-soft">
                    +{dayEvents.length - (compact ? 1 : 2)} more
                  </span>
                )}
              </span>
              <span className="mt-1 flex flex-wrap gap-1">
                {opens > 0 && <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }} />}
                {kept > 0 && <span className="h-1.5 w-1.5 rounded-full bg-forest" />}
                {practised && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
                {dayReminders.some((r) => !r.done) && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
